<template>
  <div class="stats-container">
    <h1 class="page-title">Stats for Board #{{ boardId }}</h1>
    <loading-spinner v-if="loading" />
    <div v-else class="stats-content">
      <div v-if="error" class="error-message">
        <p>{{ error }}</p>
      </div>
      <div v-else-if="stats">
        <h2>Board Statistics</h2>

        <div class="charts-container">
          <div class="chart-wrapper">
            <h3>Score Distribution from Buckets</h3>
            <div class="chart-controls">
              <label>
                <input type="radio" v-model="bucketChartMode" value="regular" />
                Regular Mode
              </label>
              <label>
                <input type="radio" v-model="bucketChartMode" value="infinite" />
                Infinite Mode
              </label>
              <label>
                <input type="radio" v-model="bucketChartMode" value="stacked" />
                Both (Stacked)
              </label>
            </div>
            <div class="chart-container">
              <div
                :class="{ hidden: bucketChartMode !== 'regular' }"
                class="fixed-height-container"
              >
                <canvas ref="regularHistogram"></canvas>
              </div>
              <div
                :class="{ hidden: bucketChartMode !== 'infinite' }"
                class="fixed-height-container"
              >
                <canvas ref="infiniteHistogram"></canvas>
              </div>
              <div
                :class="{ hidden: bucketChartMode !== 'stacked' }"
                class="fixed-height-container"
              >
                <canvas ref="stackedHistogram"></canvas>
              </div>
            </div>
            <div class="histogram-total" v-if="bucketChartMode === 'regular'">
              Total Regular Scores:
              <span class="total-value">{{ formattedRegularTotalCount }}</span>
            </div>
            <div class="histogram-total" v-if="bucketChartMode === 'infinite'">
              Total Infinite Scores:
              <span class="total-value">{{ formattedInfiniteTotalCount }}</span>
            </div>
            <div class="histogram-total" v-if="bucketChartMode === 'stacked'">
              Total Regular:
              <span class="total-value">{{ formattedRegularTotalCount }}</span>
              | Total Infinite:
              <span class="total-value">{{ formattedInfiniteTotalCount }}</span>
              | Combined:
              <span class="total-value">{{ formattedCombinedTotalCount }}</span>
            </div>
          </div>
        </div>

        <!-- New Score Ranking Section -->
        <div class="chart-wrapper score-ranking-section">
          <h3>Score Ranking Calculator</h3>
          <div class="score-input-container">
            <label for="score-input">Enter a score:</label>
            <input
              type="number"
              id="score-input"
              v-model="userScore"
              min="0"
              max="900"
              placeholder="Enter score (0-900)"
              class="score-input"
            />
          </div>

          <div class="rankings-display">
            <div class="ranking-item full-width">
              <h4>Score Rankings</h4>
              <div class="ranking-list">
                <div class="ranking-row">
                  <div class="ranking-label">New (regular):</div>
                  <div class="ranking-value">
                    <span v-if="hasRanking">{{ rankingSystem2Regular }}</span>
                    <span v-else>--</span>
                  </div>
                </div>
                <div class="ranking-row">
                  <div class="ranking-label">New (infinite):</div>
                  <div class="ranking-value">
                    <span v-if="hasRanking">{{ rankingSystem2Infinite }}</span>
                    <span v-else>--</span>
                  </div>
                </div>
                <div class="ranking-row">
                  <div class="ranking-label">New (combined):</div>
                  <div class="ranking-value">
                    <span v-if="hasRanking">{{ rankingSystem2Combined }}</span>
                    <span v-else>--</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <details>
          <summary>Show Raw JSON Data</summary>
          <pre class="stats-json">{{ JSON.stringify(stats, null, 2) }}</pre>
        </details>
      </div>
      <div v-else class="no-data-message">
        <p>No statistics data available for this board.</p>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import config from '../axiospath.js';
  import LoadingSpinner from './loadingSpinner.vue';
  import Chart from 'chart.js/auto';

  export default {
    components: {
      LoadingSpinner,
    },
    data() {
      return {
        boardId: '',
        stats: null,
        loading: true,
        error: null,
        scoresChart: null,
        regularChart: null,
        infiniteChart: null,
        stackedChart: null,
        bucketChartMode: 'stacked', // Default to stacked view
        renderTimeout: null,
        userScore: 250, // Default to 250
      };
    },
    mounted() {
      // Get the board ID from the URL
      this.boardId = this.$route.params.board_id;
      this.fetchStats();
    },
    watch: {
      // Fetch stats when boardId changes
      '$route.params.board_id'(newValue) {
        if (newValue !== this.boardId) {
          this.boardId = newValue;
          this.fetchStats();
        }
      },
    },
    computed: {
      hasRanking() {
        if (!this.stats || !this.stats.scores_by_bucket || !this.stats.scores_by_bucket.total) {
          return false;
        }

        const score = parseFloat(this.userScore);
        if (isNaN(score)) return false;

        return true;
      },

      rankingSystem2Regular() {
        const score = parseFloat(this.userScore);

        // Calculate the numerator and denominator based on the bucket data
        const { numerator, denominator } = this.calculateBucketRanking(score, 'regular');

        // Format the result with commas
        const formattedNumerator = this.formatNumberWithCommas(numerator);
        const formattedDenominator = this.formatNumberWithCommas(denominator);

        return `${formattedNumerator} / ${formattedDenominator}`;
      },

      rankingSystem2Infinite() {
        const score = parseFloat(this.userScore);

        // Calculate the numerator and denominator based on the bucket data
        const { numerator, denominator } = this.calculateBucketRanking(score, 'infinite');

        // Format the result with commas
        const formattedNumerator = this.formatNumberWithCommas(numerator);
        const formattedDenominator = this.formatNumberWithCommas(denominator);

        return `${formattedNumerator} / ${formattedDenominator}`;
      },

      rankingSystem2Combined() {
        const score = parseFloat(this.userScore);

        // Calculate the numerator and denominator based on the bucket data
        const { numerator, denominator } = this.calculateBucketRanking(score, 'combined');

        // Format the result with commas
        const formattedNumerator = this.formatNumberWithCommas(numerator);
        const formattedDenominator = this.formatNumberWithCommas(denominator);

        return `${formattedNumerator} / ${formattedDenominator}`;
      },

      formattedScoresTotalCount() {
        if (!this.stats || !this.stats.scores) return '0';
        return this.formatNumberWithCommas(this.stats.scores.length);
      },

      formattedRegularTotalCount() {
        if (!this.stats || !this.stats.scores_by_bucket || !this.stats.scores_by_bucket.total)
          return '0';

        const total = this.calculateTotalFromBuckets(this.stats.scores_by_bucket.total);
        return this.formatNumberWithCommas(total);
      },

      formattedInfiniteTotalCount() {
        if (!this.stats || !this.stats.scores_by_bucket || !this.stats.scores_by_bucket.total_inf)
          return '0';

        const total = this.calculateTotalFromBuckets(this.stats.scores_by_bucket.total_inf);
        return this.formatNumberWithCommas(total);
      },

      formattedCombinedTotalCount() {
        const regularTotal =
          this.stats && this.stats.scores_by_bucket && this.stats.scores_by_bucket.total
            ? this.calculateTotalFromBuckets(this.stats.scores_by_bucket.total)
            : 0;

        const infiniteTotal =
          this.stats && this.stats.scores_by_bucket && this.stats.scores_by_bucket.total_inf
            ? this.calculateTotalFromBuckets(this.stats.scores_by_bucket.total_inf)
            : 0;

        return this.formatNumberWithCommas(regularTotal + infiniteTotal);
      },
    },
    methods: {
      async fetchStats() {
        this.loading = true;
        this.error = null;

        try {
          const response = await axios.get(`${config.baseURL}/game/stats/${this.boardId}`);
          this.stats = response.data;

          // Wait for the DOM to update
          this.$nextTick(() => {
            this.renderCharts();
          });
        } catch (error) {
          console.error('Error fetching stats:', error);
          this.error = 'Failed to load statistics data. Please try again later.';
        } finally {
          this.loading = false;
        }
      },

      renderCharts() {
        if (this.renderTimeout) {
          clearTimeout(this.renderTimeout);
        }

        this.renderTimeout = setTimeout(() => {
          if (!this.stats) return;

          // Check if refs are available
          if (
            this.$refs.regularHistogram &&
            this.$refs.infiniteHistogram &&
            this.$refs.stackedHistogram
          ) {
            this.renderAllBucketHistograms();
          } else {
            // If not available yet, retry after a short delay
            setTimeout(() => this.renderCharts(), 100);
          }
        }, 50); // Small delay to ensure DOM is ready
      },

      renderAllBucketHistograms() {
        const { regularData, infiniteData, infiniteDataAdjusted } = this.processBucketData();

        // Render Regular Mode Chart
        this.renderRegularHistogram(regularData);

        // Render Infinite Mode Chart
        this.renderInfiniteHistogram(infiniteDataAdjusted);

        // Render Stacked Chart
        this.renderStackedHistogram(regularData, infiniteData);
      },

      renderRegularHistogram(regularData) {
        if (this.regularChart) {
          this.regularChart.destroy();
          this.regularChart = null;
        }

        const canvas = this.$refs.regularHistogram;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        this.regularChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: [
              '0-100',
              '101-200',
              '201-300',
              '301-400',
              '401-500',
              '501-600',
              '601-700',
              '701-800',
              '801-900',
            ],
            datasets: [
              {
                label: 'Regular Mode',
                data: regularData,
                backgroundColor: 'rgba(54, 162, 235, 0.6)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
                minBarLength: 5,
              },
            ],
          },
          options: {
            animation: {
              duration: 0, // Disable animation for faster rendering
            },
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Count',
                  color: 'rgb(223, 223, 223)',
                },
                ticks: {
                  color: 'rgb(223, 223, 223)',
                },
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)',
                },
              },
              x: {
                title: {
                  display: true,
                  text: 'Score Range',
                  color: 'rgb(223, 223, 223)',
                },
                ticks: {
                  color: 'rgb(223, 223, 223)',
                },
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)',
                },
              },
            },
            plugins: {
              legend: {
                labels: {
                  color: 'rgb(223, 223, 223)',
                },
              },
              tooltip: {
                callbacks: {
                  title: function (tooltipItems) {
                    return tooltipItems[0].label;
                  },
                  label: function (context) {
                    return `Count: ${context.raw}`;
                  },
                },
              },
            },
          },
        });
      },

      renderInfiniteHistogram(infiniteData) {
        if (this.infiniteChart) {
          this.infiniteChart.destroy();
          this.infiniteChart = null;
        }

        const canvas = this.$refs.infiniteHistogram;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const labels = [];
        for (let i = 0; i < infiniteData.length; i++) {
          if (i < 10) {
            labels.push(`${i * 25}-${(i + 1) * 25}`);
          } else {
            labels.push(`${250 + (i - 10) * 325}-${250 + (i - 9) * 325}`);
          }
        }

        this.infiniteChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels,
            datasets: [
              {
                label: 'Infinite Mode',
                data: infiniteData,
                backgroundColor: 'rgba(255, 99, 132, 0.6)',
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 1,
                minBarLength: 5,
              },
            ],
          },
          options: {
            animation: {
              duration: 0,
            },
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Count',
                  color: 'rgb(223, 223, 223)',
                },
                ticks: {
                  color: 'rgb(223, 223, 223)',
                },
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)',
                },
              },
              x: {
                title: {
                  display: true,
                  text: 'Score Range',
                  color: 'rgb(223, 223, 223)',
                },
                ticks: {
                  color: 'rgb(223, 223, 223)',
                },
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)',
                },
              },
            },
            plugins: {
              legend: {
                labels: {
                  color: 'rgb(223, 223, 223)',
                },
              },
              tooltip: {
                callbacks: {
                  title: function (tooltipItems) {
                    return tooltipItems[0].label;
                  },
                  label: function (context) {
                    return `Count: ${context.raw}`;
                  },
                },
              },
            },
          },
        });
      },

      renderStackedHistogram(regularData, infiniteData) {
        if (this.stackedChart) {
          this.stackedChart.destroy();
          this.stackedChart = null;
        }

        const canvas = this.$refs.stackedHistogram;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        this.stackedChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: [
              '0-100',
              '101-200',
              '201-300',
              '301-400',
              '401-500',
              '501-600',
              '601-700',
              '701-800',
              '801-900',
            ],
            datasets: [
              {
                label: 'Regular Mode',
                data: regularData,
                backgroundColor: 'rgba(54, 162, 235, 0.6)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
                minBarLength: 5,
              },
              {
                label: 'Infinite Mode',
                data: infiniteData,
                backgroundColor: 'rgba(255, 99, 132, 0.6)',
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 1,
                minBarLength: 5,
              },
            ],
          },
          options: {
            animation: {
              duration: 0,
            },
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                stacked: true,
                title: {
                  display: true,
                  text: 'Count',
                  color: 'rgb(223, 223, 223)',
                },
                ticks: {
                  color: 'rgb(223, 223, 223)',
                },
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)',
                },
              },
              x: {
                stacked: true,
                title: {
                  display: true,
                  text: 'Score Range',
                  color: 'rgb(223, 223, 223)',
                },
                ticks: {
                  color: 'rgb(223, 223, 223)',
                },
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)',
                },
              },
            },
            plugins: {
              legend: {
                labels: {
                  color: 'rgb(223, 223, 223)',
                },
              },
              tooltip: {
                callbacks: {
                  title: function (tooltipItems) {
                    return tooltipItems[0].label;
                  },
                  label: function (context) {
                    const datasetLabel = context.dataset.label || '';
                    return `${datasetLabel}: ${context.raw}`;
                  },
                  footer: function (tooltipItems) {
                    // We can access the dataset directly from the chart data
                    // Get the index of the hovered bar
                    const dataIndex = tooltipItems[0].dataIndex;

                    // Access both datasets at this index
                    const regularData = tooltipItems[0].chart.data.datasets[0].data[dataIndex] || 0;
                    const infiniteData =
                      tooltipItems[0].chart.data.datasets[1].data[dataIndex] || 0;

                    const total = regularData + infiniteData;

                    return `Total: ${total}`;
                  },
                },
              },
            },
          },
        });
      },

      bucketScores(scores) {
        const buckets = [0, 0, 0, 0, 0, 0, 0, 0, 0]; // 9 buckets

        scores.forEach((score) => {
          const bucketIndex = Math.min(Math.floor(score / 100), 8);
          buckets[bucketIndex]++;
        });

        return buckets;
      },

      processBucketData() {
        const regularBuckets = [0, 0, 0, 0, 0, 0, 0, 0, 0]; // 9 buckets
        const infiniteBuckets = [0, 0, 0, 0, 0, 0, 0, 0, 0]; // 9 buckets
        const infiniteAdjustedBuckets = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]; // 12 buckets

        if (!this.stats.scores_by_bucket || !this.stats.scores_by_bucket.total) {
          return {
            regularData: regularBuckets,
            infiniteData: infiniteBuckets,
            infiniteDataAdjusted: infiniteAdjustedBuckets,
          };
        }

        // Process regular mode data
        const totalData = this.stats.scores_by_bucket.total;
        Object.keys(totalData).forEach((key) => {
          if (key === '_divisor') return;

          const minScore = parseInt(key) * 9;
          const count = totalData[key];
          const gap = 9 / count;

          for (let i = 0; i < count; i++) {
            const score = minScore + gap * i;
            const bucketIndex = Math.min(Math.floor(score / 100), 8);
            regularBuckets[bucketIndex] += 1;
          }
        });

        // Process infinite mode data
        const totalInfData = this.stats.scores_by_bucket.total_inf;
        Object.keys(totalInfData).forEach((key) => {
          if (key === '_divisor') return;

          const minScore = parseInt(key) * 9;
          const count = totalInfData[key];
          const gap = 9 / count;

          for (let i = 0; i < count; i++) {
            const score = minScore + gap * i;

            // Normal
            {
              const bucketIndex = Math.min(Math.floor(score / 100), 8);
              infiniteBuckets[bucketIndex] += 1;
            }
            // Adjusted
            {
              if (score < 250) {
                // Buckets of width 25 up to 250
                const bucketIndex = Math.floor(score / 25);
                infiniteAdjustedBuckets[bucketIndex] += 1;
              } else {
                if (score < 575) {
                  infiniteAdjustedBuckets[10] += 1;
                } else {
                  infiniteAdjustedBuckets[11] += 1;
                }
              }
            }
          }
        });

        return {
          regularData: regularBuckets,
          infiniteData: infiniteBuckets,
          infiniteDataAdjusted: infiniteAdjustedBuckets,
        };
      },

      calculateBucketRanking(score, method) {
        // Get the total bucket data
        let totalBuckets = {};
        if (method == 'regular') {
          totalBuckets = this.stats.scores_by_bucket.total;
        } else if (method == 'infinite') {
          totalBuckets = this.stats.scores_by_bucket.total_inf;
        } else {
          // Combine both regular and infinite buckets
          const regularBuckets = this.stats.scores_by_bucket.total;
          const infiniteBuckets = this.stats.scores_by_bucket.total_inf;
          Object.keys(infiniteBuckets).forEach((key) => {
            if (key === '_divisor') return;
            totalBuckets[key] = (totalBuckets[key] || 0) + infiniteBuckets[key];
          });
          Object.keys(regularBuckets).forEach((key) => {
            if (key === '_divisor') return;
            totalBuckets[key] = (totalBuckets[key] || 0) + regularBuckets[key];
          });
        }

        // Calculate the pivot value - score divided by 9
        const pivotValue = Math.floor(score / 9);

        let numerator = 0;
        let denominator = 0;

        // Calculate the denominator and numerator
        Object.keys(totalBuckets).forEach((key) => {
          // Skip the _divisor key
          if (key === '_divisor') return;

          const keyValue = parseInt(key);
          const frequency = totalBuckets[key];

          // Add to the denominator (sum of all frequencies)
          denominator += frequency;

          // Add to the numerator if the key is less than the pivot value
          if (keyValue < pivotValue) {
            numerator += frequency;
          } else if (keyValue == pivotValue) {
            const percentageBetter = score / 9 - pivotValue;
            numerator += Math.floor(frequency * percentageBetter);
          }
        });

        // Add 1 to the numerator to make it 1-based instead of 0-based
        // This ensures the result is comparable to rankingSystem1
        numerator += 1;
        denominator += 1;

        return { numerator, denominator };
      },

      formatNumberWithCommas(number) {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      },

      calculateTotalFromBuckets(bucketData) {
        let total = 0;

        Object.keys(bucketData).forEach((key) => {
          if (key === '_divisor') return;
          total += bucketData[key];
        });

        return total;
      },
    },
    beforeUnmount() {
      if (this.regularChart) {
        this.regularChart.destroy();
      }
      if (this.infiniteChart) {
        this.infiniteChart.destroy();
      }
      if (this.stackedChart) {
        this.stackedChart.destroy();
      }
      if (this.renderTimeout) {
        clearTimeout(this.renderTimeout);
      }
    },
  };
</script>

<style scoped>
  .stats-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    color: rgb(223, 223, 223);
  }

  .page-title {
    color: rgb(223, 223, 223);
    text-align: center;
    margin-bottom: 30px;
  }

  .stats-content {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    padding: 20px;
  }

  .charts-container {
    display: flex;
    flex-direction: column;
    gap: 30px;
    margin-bottom: 30px;
  }

  .chart-wrapper {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 20px;
  }

  .chart-wrapper h3 {
    text-align: center;
    margin-bottom: 15px;
    color: rgb(223, 223, 223);
  }

  details {
    margin-top: 20px;
  }

  summary {
    cursor: pointer;
    padding: 10px;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 5px;
    margin-bottom: 10px;
  }

  .stats-json {
    background-color: rgba(0, 0, 0, 0.3);
    padding: 15px;
    border-radius: 5px;
    overflow-x: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
    color: rgb(223, 223, 223);
    font-family: monospace;
  }

  .error-message,
  .no-data-message {
    text-align: center;
    padding: 20px;
  }

  .error-message {
    color: #f2314e;
  }

  .chart-controls {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-bottom: 15px;
    flex-wrap: wrap;
  }

  .chart-controls label {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    padding: 5px 10px;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    transition: background-color 0.2s;
  }

  .chart-controls label:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .chart-controls input[type='radio'] {
    margin: 0;
  }

  .chart-container {
    position: relative;
    height: 400px;
  }

  .fixed-height-container {
    height: 350px;
    width: 100%;
  }

  .chart-container > div {
    position: absolute;
    width: 100%;
    height: 100%;
  }

  .hidden {
    display: none;
  }

  @media (min-width: 768px) {
    .charts-container {
      flex-direction: row;
      flex-wrap: wrap;
    }

    .chart-wrapper {
      flex: 1 1 45%;
      min-width: 300px;
    }
  }

  @media (max-width: 600px) {
    .chart-controls {
      flex-direction: column;
      align-items: center;
    }
  }

  /* New styles for the score ranking section */
  .score-ranking-section {
    margin-top: 30px;
  }

  .score-input-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .score-input {
    padding: 10px;
    border-radius: 5px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background-color: rgba(0, 0, 0, 0.3);
    color: rgb(223, 223, 223);
    font-size: 16px;
    width: 150px;
  }

  .rankings-display {
    display: flex;
    flex-wrap: wrap;
    gap: 30px;
    justify-content: space-around;
    margin-top: 20px;
  }

  .ranking-item {
    flex: 1 1 45%;
    min-width: 250px;
    background-color: rgba(0, 0, 0, 0.2);
    padding: 15px;
    border-radius: 8px;
  }

  .full-width {
    flex: 1 1 100%;
  }

  .ranking-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 550px;
    margin: auto;
  }

  .ranking-row {
    display: flex;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 5px;
    padding: 10px 15px;
  }

  .ranking-label {
    flex: 0 0 40%;
    font-weight: bold;
    text-align: right;
    padding-right: 15px;
  }

  .ranking-value {
    flex: 0 0 60%;
  }

  @media (max-width: 600px) {
    .score-input-container {
      flex-direction: column;
    }

    .ranking-item {
      flex: 1 1 100%;
    }
  }

  .histogram-total {
    text-align: center;
    margin-top: 15px;
    padding: 8px;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    font-size: 0.95rem;
  }

  .total-value {
    font-weight: bold;
    color: rgba(255, 255, 255, 0.9);
  }
</style>
